import type { FaceParts } from "./renderer";

export class RateLimitError extends Error {
  constructor(readonly retryAfter: number) {
    super("Too many questions");
  }
}

export interface SiteConfig {
  id: string;
  business_name: string;
  assistant: { name: string };
  branding?: boolean; // show the mugam.site badge (owners can pay to remove it)
  accent?: string;
  avatar_version: number;
  greeting: { text: string; clip: string | null } | null;
  suggestions: string[];
  suggestion_topics?: Suggestion[];
}

export interface Suggestion {
  question: string;
  paraphrases?: string[];
  page_url?: string;
  anchor?: string;
}

export interface Answer {
  text: string;
  clip: string | null;
  page_url?: string;
  anchor?: string;
  matched?: boolean;
  answered_question?: string;
  action?: Action | null;
}

/** What the owner set to follow this answer: a link button, or a short form. */
export type Action =
  | { type: "link"; label: string; url: string }
  | { type: "form"; qa_id: number; title: string; button: string; fields: FormField[] };

export interface FormField {
  id: string;
  label: string;
  kind: "text" | "email" | "tel" | "date" | "textarea";
  required: boolean;
}

export interface LeadIn {
  qa_id: number;
  values: Record<string, string>;
  question: string;
  page_url: string;
  website: string; // honeypot, always empty for people
}

async function loadImage(src: string): Promise<HTMLImageElement> {
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.src = src;
  await img.decode();
  return img;
}

export class Api {
  constructor(private readonly base: string, private readonly t: (message: string) => string = (message) => message) {}

  /** Absolute URLs (clips on the CDN, blob: URLs) pass through; paths are relative to the API server. */
  url(path: string): string {
    return /^[a-z][a-z\d+.-]*:/i.test(path) ? path : this.base + path;
  }

  async site(id: string): Promise<SiteConfig> {
    // credentials: owners can preview their own site before it goes live.
    const r = await fetch(this.url(`/api/sites/${encodeURIComponent(id)}`), { credentials: "same-origin" });
    if (!r.ok) throw new Error(`site ${id}: HTTP ${r.status}`);
    return r.json();
  }

  async avatar(site: SiteConfig): Promise<{ img: HTMLImageElement; parts: FaceParts; sheet: HTMLImageElement | null }> {
    const base = this.url(`/api/sites/${encodeURIComponent(site.id)}/avatar`);
    const v = `?v=${site.avatar_version}`;
    const [parts, img] = await Promise.all([
      fetch(`${base}.json${v}`, { credentials: "same-origin" }).then((r) => {
        if (!r.ok) throw new Error(`avatar: HTTP ${r.status}`);
        return r.json() as Promise<FaceParts>;
      }),
      loadImage(`${base}.png${v}`),
    ]);
    const sheet = parts.sprites ? await loadImage(`${base}.sprites.png${v}`).catch(() => null) : null;
    return { img, parts, sheet };
  }

  async ask(site: string, question: string, signal?: AbortSignal): Promise<Answer> {
    const r = await fetch(this.url("/api/ask"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ site, question }),
      credentials: "same-origin",
      signal,
    });
    if (r.status === 429) {
      const retry = Number(r.headers.get("Retry-After"));
      throw new RateLimitError(Number.isFinite(retry) && retry > 0 ? retry : 60);
    }
    if (!r.ok) throw new Error(`ask: HTTP ${r.status}`);
    return r.json();
  }

  /** Send a visitor's form to the site owner. Throws with a message the visitor can read. */
  async submitLead(site: string, lead: LeadIn): Promise<string> {
    const r = await fetch(this.url(`/api/sites/${encodeURIComponent(site)}/leads`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(lead),
      credentials: "same-origin",
    });
    const data = await r.json().catch(() => ({}));
    if (r.status === 429) throw new Error(this.t("Please wait a few minutes before sending again."));
    if (!r.ok) throw new Error(typeof data.detail === "string" ? data.detail : this.t("Couldn't send. Please try again."));
    // The server's default receipt contains the business name. Localize the
    // interface receipt, while preserving any separately authored message.
    const message = typeof data.message === "string" ? data.message : "";
    return !message || /^Thanks! .+ will get back to you\.$/.test(message)
      ? this.t("Thanks! We'll get back to you.") : this.t(message);
  }
}
